import React, { useCallback, useRef } from 'react';
import { GoLinkExternal } from 'react-icons/go';
import Select from 'react-select';
import Button from '../Button';
import { formSelectStyles, formSelectControlFullWidth } from '../FormInput/formSelectStyles';

/** Estilos do FilterBar: control 40px + centralização vertical do texto via API do react-select */
const dashboardFilterSelectStyles = {
    control: (base, state) => ({
        ...formSelectControlFullWidth(base, state),
        minHeight: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #ECECF3',
        color: '#7F7F8A',
    }),
    valueContainer: (base) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minHeight: 0,
        padding: '0 8px 0 0',
    }),
    singleValue: (base) => ({
        ...base,
        margin: 0,
        position: 'relative',
        top: 'auto',
        transform: 'none',
        lineHeight: '40px',
        color: '#7F7F8A',
    }),
    placeholder: (base) => ({
        ...base,
        margin: 0,
        position: 'relative',
        top: 'auto',
        transform: 'none',
        lineHeight: '40px',
        color: '#7F7F8A',
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        color: '#7F7F8A',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        alignSelf: 'stretch',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: '0 8px',
        display: 'flex',
        alignItems: 'center',
        color: '#7F7F8A',
    }),
};

const FilterBar = React.memo(({ 
    crops,
    selectedCrop,
    operations,
    selectedOperation,
    modulesFiltered,
    selectedModule,
    statusFiltered,
    selectedStatus,
    version,
    selectedVersion,
    userObject,
    showOperationModuleFilters,
    onCropChange,
    onSearchChange,
    onOperationChange,
    onModuleChange,
    onStatusChange,
    onVersionChange,
    onCollapseAll,
    onExpandAll,
    onRedirectBI
}) => {
    const searchTimeoutRef = useRef(null);

    const handleSearchChange = useCallback((e) => {
        const searchValue = e.target.value;
        
        // Debounce search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            onSearchChange(searchValue);
        }, 300);
    }, [onSearchChange]);

    return (
        <div className="container dashboard-body">
            <div className="dashboard-body-title row">
                <div className="col-md-12">
                    <p>Filter</p>
                </div>
            </div>
            <div className="dashboard-body-filters row">
                <div className="col-md-2">
                    <Select
                        options={crops?.map((y) => ({ value: y.reportYearID, label: y.year })) ?? []}
                        value={crops?.find((y) => y.reportYearID === selectedCrop) ? { value: selectedCrop, label: crops.find((y) => y.reportYearID === selectedCrop).year } : null}
                        onChange={(e) => onCropChange(e?.value ?? '')}
                        placeholder="Crop"
                        isSearchable
                        isClearable={false}
                        styles={{ ...formSelectStyles, ...dashboardFilterSelectStyles }}
                        classNamePrefix="filterbar-crop"
                    />
                </div>
                <div className="col-md-10">
                    <input
                        type="text"
                        placeholder="Search Supplier"
                        onChange={handleSearchChange}
                    />
                </div>
                {showOperationModuleFilters && (
                    <>
                        <div className="col-md-4">
                            <Select
                                options={operations?.length ? [{ value: '', label: 'All Forms' }, ...operations.map((o) => ({ value: o.operationID, label: o.description }))] : []}
                                value={selectedOperation === '' || selectedOperation == null ? { value: '', label: 'All Forms' } : (operations?.find((o) => o.operationID === selectedOperation) ? { value: selectedOperation, label: operations.find((o) => o.operationID === selectedOperation).description } : { value: selectedOperation, label: 'All Forms' })}
                                onChange={(e) => onOperationChange(e?.value ?? '')}
                                placeholder="All Forms"
                                isSearchable
                                isClearable={false}
                                styles={{ ...formSelectStyles, ...dashboardFilterSelectStyles }}
                                classNamePrefix="filterbar-operation"
                            />
                        </div>
                        <div className="col-md-4">
                            <Select
                                options={modulesFiltered?.length ? [{ value: '', label: 'All Modules' }, ...modulesFiltered.map((m) => ({ value: m.moduleID, label: m.description }))] : []}
                                value={selectedModule === '' || selectedModule == null ? { value: '', label: 'All Modules' } : (modulesFiltered?.find((m) => m.moduleID === selectedModule) ? { value: selectedModule, label: modulesFiltered.find((m) => m.moduleID === selectedModule).description } : { value: selectedModule, label: 'All Modules' })}
                                onChange={(e) => onModuleChange(e?.value ?? '')}
                                placeholder="All Modules"
                                isSearchable
                                isClearable={false}
                                styles={{ ...formSelectStyles, ...dashboardFilterSelectStyles }}
                                classNamePrefix="filterbar-module"
                            />
                        </div>
                    </>
                )}
                <div className="col-md-2">
                    <Select
                        options={[{ value: '', label: 'All Status' }, ...(statusFiltered?.map((s) => ({ value: s.value, label: s.text })) ?? [])]}
                        value={selectedStatus === '' || selectedStatus == null ? { value: '', label: 'All Status' } : (statusFiltered?.find((s) => s.value === selectedStatus) ? { value: selectedStatus, label: statusFiltered.find((s) => s.value === selectedStatus).text } : { value: selectedStatus, label: 'All Status' })}
                        onChange={(e) => onStatusChange(e?.value ?? '')}
                        placeholder="All Status"
                        isSearchable
                        isClearable={false}
                        styles={{ ...formSelectStyles, ...dashboardFilterSelectStyles }}
                        classNamePrefix="filterbar-status"
                    />
                </div>
                <div className="col-md-2">
                    <Select
                        options={version?.length ? [{ value: '', label: 'Partial/Final' }, ...version.map((v) => ({ value: v.value, label: v.text }))] : []}
                        value={selectedVersion === '' || selectedVersion == null ? { value: '', label: 'Partial/Final' } : (version?.find((v) => v.value === selectedVersion) ? { value: selectedVersion, label: version.find((v) => v.value === selectedVersion).text } : { value: selectedVersion, label: 'Partial/Final' })}
                        onChange={(e) => onVersionChange(e?.value ?? '')}
                        placeholder="Partial/Final"
                        isSearchable
                        isClearable={false}
                        styles={{ ...formSelectStyles, ...dashboardFilterSelectStyles }}
                        classNamePrefix="filterbar-version"
                    />
                </div>
                <div className="buttons-collapse">
                    <div className="dashboard-buttons">
                        {/* {userObject && (userObject.isSourcingGroup == null || userObject.isSourcingGroup === false) && (
                            <button 
                                type="button" 
                                style={{ width: "170px", fontSize: "11px", float: "right", margin: "2.92rem 0rem 0rem 0" }} 
                                onClick={onRedirectBI} 
                                className="btn btn-custom"
                            >
                                see dashboard <GoLinkExternal size={17} />
                            </button>
                        )} */}
                    </div>
                    <Button 
                        style={{ width: "130px", fontSize: "11px", float: "right", margin: "3rem 1rem 0rem 0" }} 
                        id="collapseAll" 
                        onClick={onCollapseAll}
                    >
                        Collapse All
                    </Button>
                    <Button 
                        style={{ margin: "3rem 1rem 0rem 0", width: "130px", fontSize: "11px", float: "right" }} 
                        id="expandAll" 
                        onClick={onExpandAll}
                    >
                        Expand All
                    </Button>
                </div>
            </div>
        </div>
    );
});

FilterBar.displayName = 'FilterBar';

export default FilterBar;


